'use client';

import React from 'react';
import { useTranslation } from 'react-i18next';
import Link from 'next/link';
import { buttonVariants } from '@/components/ui/Button';
import { Rocket, TrendingUp, Award, GraduationCap, Check, ArrowRight } from 'lucide-react';
import { useIntersectionObserver } from '@/app/hooks/useIntersectionObserver';

export default function PartnersContent() {
  const { t } = useTranslation();
  const { ref: heroRef, isVisible: heroVisible } = useIntersectionObserver();
  const { ref: typesRef, isVisible: typesVisible } = useIntersectionObserver();
  const { ref: benefitsRef, isVisible: benefitsVisible } = useIntersectionObserver();
  const { ref: ctaRef, isVisible: ctaVisible } = useIntersectionObserver();

  const partnerTypes = [
    { key: 'accelerators', icon: <Rocket className="h-10 w-10 text-primary-500" /> },
    { key: 'vcNetworks', icon: <TrendingUp className="h-10 w-10 text-primary-500" /> },
    { key: 'startupPrograms', icon: <Award className="h-10 w-10 text-primary-500" /> },
    { key: 'universities', icon: <GraduationCap className="h-10 w-10 text-primary-500" /> },
  ];

  // Translated, not hardcoded — see pageContent.partners.benefits.items.
  //
  // Three entries were removed rather than translated because nothing backs
  // them: "Analytics and reporting on pitch performance" (opens and replies are
  // never tracked — opened_at/replied_at are never written), "Custom
  // integration with your existing workflow" (no integration surface exists),
  // and "Revenue sharing opportunities" (no affiliate or revenue-share
  // mechanism exists). Do not reinstate any of them without an implementation.
  const benefitsRaw = t('pageContent.partners.benefits.items', { returnObjects: true });
  const benefits: string[] = Array.isArray(benefitsRaw) ? (benefitsRaw as string[]) : [];

  return (
    <>
      {/* Hero Section */}
      <section className="py-20 bg-gray-50">
        <div className="container">
          <div
            ref={heroRef}
            className={`max-w-3xl mx-auto text-center fade-in-section ${heroVisible ? 'is-visible' : ''}`}
          >
            <span className="inline-block bg-primary-50 text-primary-600 px-4 py-2 rounded-full text-sm font-semibold mb-4">
              {t('pageContent.partners.hero.tagline')}
            </span>
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4 break-words">
              {t('pageContent.partners.hero.title')}
            </h1>
            <p className="text-xl text-gray-600 mb-12">
              {t('pageContent.partners.hero.subtitle')}
            </p>
          </div>
        </div>
      </section>

      {/* Partner Types */}
      <section className="py-20">
        <div className="container">
          <div
            ref={typesRef}
            className={`text-center mb-12 fade-in-section ${typesVisible ? 'is-visible' : ''}`}
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4 break-words">
              {t('pageContent.partners.types.title')}
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {partnerTypes.map((type, index) => (
              <div
                key={type.key}
                className={`bg-white rounded-xl shadow-sm border border-gray-100 p-8 hover:shadow-md transition-shadow hover-lift fade-in-section ${typesVisible ? 'is-visible' : ''}`}
                style={{ transitionDelay: `${index * 0.15}s` }}
              >
                <div className="mb-4">{type.icon}</div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  {t(`pageContent.partners.types.${type.key}.title`)}
                </h3>
                <p className="text-gray-600">
                  {t(`pageContent.partners.types.${type.key}.description`)}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-20 bg-gray-50">
        <div className="container">
          <div
            ref={benefitsRef}
            className={`max-w-3xl mx-auto fade-in-section ${benefitsVisible ? 'is-visible' : ''}`}
          >
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                {t('pageContent.partners.benefits.title')}
              </h2>
            </div>
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-8 md:p-10">
              <ul className="space-y-4">
                {benefits.map((benefit, index) => (
                  <li
                    key={index}
                    className={`flex items-start fade-in-section ${benefitsVisible ? 'is-visible' : ''}`}
                    style={{ transitionDelay: `${index * 0.1}s` }}
                  >
                    <Check className="h-6 w-6 text-primary-500 flex-shrink-0 mr-3 mt-0.5" />
                    <span className="text-gray-700">{benefit}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-gradient-to-r from-primary-500 to-primary-600 text-white">
        <div className="container">
          <div
            ref={ctaRef}
            className={`max-w-3xl mx-auto text-center fade-in-section ${ctaVisible ? 'is-visible' : ''}`}
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              {t('pageContent.partners.cta.title')}
            </h2>
            <p className="text-xl text-primary-100 mb-8">
              {t('pageContent.partners.cta.subtitle')}
            </p>
            <Link
              href="/contact"
              className={buttonVariants({ variant: 'secondary', size: 'lg', className: 'px-8 hover-scale' })}
            >
              {t('pageContent.partners.cta.button')}
              <ArrowRight className="ml-2 h-5 w-5" />
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
